import { fromJS, List } from 'immutable';
import { CallRecordingSettings, SettingsType } from 'flow/types/CallRecording';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import {
    callRecordingVaries,
    monitoringVaries,
    personalCallRecordingVaries,
} from './profile-utils';

export type ProfileToggleListSettings = {
    keyName: 'recordCalls' | 'personalRecordCalls' | 'canBeCoMonitored';
    headerName:
        | 'settings.call_recording'
        | 'settings.call_recording_personal'
        | 'settings.call_monitoring';
    descriptionName:
        | 'settings.record_calls'
        | 'settings.record_personal_calls'
        | 'settings.allow_call_monitoring';
    profilesVary: (ownProfiles: List<TypedMap<Profile>>) => boolean;
};

const recordingSettings: ProfileToggleListSettings = {
    keyName: 'recordCalls',
    headerName: 'settings.call_recording',
    descriptionName: 'settings.record_calls',
    profilesVary: callRecordingVaries,
};

const personalRecordingSettings: ProfileToggleListSettings = {
    keyName: 'personalRecordCalls',
    headerName: 'settings.call_recording_personal',
    descriptionName: 'settings.record_personal_calls',
    profilesVary: personalCallRecordingVaries,
};

const monitoringSettings: ProfileToggleListSettings = {
    keyName: 'canBeCoMonitored',
    headerName: 'settings.call_monitoring',
    descriptionName: 'settings.allow_call_monitoring',
    profilesVary: monitoringVaries,
};

export const getRecordAndMonitoringSettings = (settingType: SettingsType) => {
    const settings = {
        business: recordingSettings,
        personal: personalRecordingSettings,
        monitoring: monitoringSettings,
    };

    return settings[settingType];
};

export const keysMappedBySettingsType: Record<
    SettingsType,
    Record<'recordCalls' | 'transcribe', keyof Profile>
> = {
    business: {
        recordCalls: 'recordCalls',
        transcribe: 'autoTranscribe',
    },
    personal: {
        recordCalls: 'personalRecordCalls',
        transcribe: 'personalAutoTranscribe',
    },
    monitoring: {
        recordCalls: null,
        transcribe: null,
    },
};

export const getCallRecordingTitle = (profile: TypedMap<Profile>) => {
    if (
        profile?.has('autoTranscribe') ||
        profile?.has('personalAutoTranscribe')
    ) {
        return 'settings.call_recording_and_transcription';
    }

    return 'settings.call_recording_and_monitoring';
};

export const getCallRecordingValue = (
    profile: TypedMap<Profile>,
    settingsType: SettingsType,
    enableAutoTranscribeWithoutRecordedCall = false,
) => {
    const { recordCalls, transcribe } = keysMappedBySettingsType[settingsType];

    if (profile.get(recordCalls) && profile.get(transcribe)) {
        return CallRecordingSettings.RecordAndAutoTranscribe;
    }

    if (profile.get(recordCalls)) {
        return CallRecordingSettings.RecordCalls;
    }

    if (enableAutoTranscribeWithoutRecordedCall && profile.get(transcribe)) {
        return CallRecordingSettings.AutoTranscribeOnly;
    }

    return CallRecordingSettings.Off;
};

export const getProfileKeysBySettingAndType = (
    setting: CallRecordingSettings,
    settingsType: SettingsType,
) => {
    if (setting === CallRecordingSettings.RecordAndAutoTranscribe) {
        return {
            [keysMappedBySettingsType[settingsType].recordCalls]: true,
            [keysMappedBySettingsType[settingsType].transcribe]: true,
        };
    }

    if (setting === CallRecordingSettings.RecordCalls) {
        return {
            [keysMappedBySettingsType[settingsType].recordCalls]: true,
            [keysMappedBySettingsType[settingsType].transcribe]: false,
        };
    }

    if (setting === CallRecordingSettings.AutoTranscribeOnly) {
        return {
            [keysMappedBySettingsType[settingsType].recordCalls]: false,
            [keysMappedBySettingsType[settingsType].transcribe]: true,
        };
    }

    return {
        [keysMappedBySettingsType[settingsType].recordCalls]: false,
        [keysMappedBySettingsType[settingsType].transcribe]: false,
    };
};

export const getUpdatedProfileDTO = (
    profile: TypedMap<Profile>,
    setting: CallRecordingSettings,
    settingsType: SettingsType,
) => {
    const valueMap = getProfileKeysBySettingAndType(setting, settingsType);

    let profileDTO = { key: profile.get('key') };

    Object.entries(valueMap).forEach(([key, value]) => {
        if (profile.has(key as keyof Profile)) {
            profileDTO = { ...profileDTO, [key]: value };
        }
    });

    return fromJS(profileDTO) as TypedMap<Profile>;
};
